import React from "react";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { InfoIcon } from "@commonsku/styles";
import { useReportContext } from "../../report_context";
import { formatMoney, parseMysqlDate } from "../../../../utils";

const OrderValueHeader = () => {
  const { query } = useReportContext();
  return (
    <span style={{ display: "flex", gap: 4 }}>
      <InfoIcon
        data-tip={
          +query["is_aggregate"] === 1
            ? "The higher of the Retail Value and Client Value, used to calculate the applicable Shop fee."
            : "Order Value is used to calculate applicable Shop fee."
        }
        data-for="report-table-tooltip"
        data-html={false}
        altText=""
      />
      Order Value
    </span>
  );
};

const DateCreatedColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> = {
  header: "Created",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_created"],
  accessorKey: "date_created",
  canSort: true,
  orderByField: "date_created",
  width: 80,
  justify: "flex-start",
  dataTransform: ({ date_created }) => ({
    date: (() => {
      const parsedDate = parseMysqlDate(date_created);
      return Array.isArray(parsedDate) ? parsedDate.reverse().join("-") : parsedDate;
    })(),
  }),
};

const ReferenceColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Reference",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["customer_po", "parent_id"],
  accessorKey: "customer_po",
  canSort: true,
  orderByField: "customer_po",
  width: 80,
  dataTransform: ({ customer_po, parent_id }) => ({
    href: `/public_order.php?order_id=${parent_id}`,
    text: customer_po,
  }),
  isVisible: (query: {}) => +query["is_aggregate"] === 1,
};

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Project Name",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number", "job_name"],
  accessorKey: "project_name",
  canSort: true,
  orderByField: "project_name",
  width: 120,
  dataTransform: ({ job_number, job_name }) => ({
    href: `/project/${job_number}`,
    text: `#${job_number} - ${job_name}`,
  }),
  isVisible: (query: {}) => +query["is_aggregate"] === 0,
};

const EmailColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Email",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["checked_out_email", "billing_contact_email"],
  accessorKey: "email",
  canSort: true,
  orderByField: "email",
  width: 250,
  dataTransform: ({ checked_out_email, billing_contact_email }) => ({
    text: checked_out_email || billing_contact_email || "",
  }),
};

const RetailValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: (
    <span style={{ display: "flex", gap: 4 }}>
      <InfoIcon
        data-tip="The subtotal value of the checkout (before shipping and taxes). This reflects the price paid by the end customer."
        data-for="report-table-tooltip"
        data-html={false}
        altText=""
      />
      Retail Value
    </span>
  ),
  type: BASE_COLUMN_TYPES.Text,
  fields: ["retail_subtotal", "charged_currency"],
  accessorKey: "retail_value",
  canSort: true,
  orderByField: "retail_value",
  width: 100,
  justify: "flex-end",
  dataTransform: ({ retail_subtotal, charged_currency }) => ({
    text: `${formatMoney(retail_subtotal)} ${charged_currency}`
  }),
  isVisible: (query: {}) => +query["is_aggregate"] === 1,
};

const ClientValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: (
    <span style={{ display: "flex", gap: 4 }}>
      <InfoIcon
        data-tip="The sales order value based on cost plus margin. This represents the amount billed to your client."
        data-for="report-table-tooltip"
        data-html={false}
        altText=""
      />
      Client Value
    </span>
  ),
  type: BASE_COLUMN_TYPES.Text,
  fields: ["client_subtotal", "charged_currency"],
  accessorKey: "client_value",
  canSort: true,
  orderByField: "client_value",
  width: 100,
  justify: "flex-end",
  dataTransform: ({ client_subtotal, charged_currency }) => ({
    text: `${formatMoney(client_subtotal)} ${charged_currency}`
  }),
  isVisible: (query: {}) => +query["is_aggregate"] === 1,
};

const OrderValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: <OrderValueHeader />,
  type: BASE_COLUMN_TYPES.Text,
  fields: ["retail_subtotal", "client_subtotal", "charged_currency"],
  accessorKey: "order_value",
  canSort: true,
  orderByField: "order_value",
  width: 100,
  justify: "flex-end",
  dataTransform: ({ retail_subtotal, client_subtotal, charged_currency }) => {
    const retail_value = parseFloat(retail_subtotal);
    const client_value = parseFloat(client_subtotal);

    if (!isNaN(retail_value) && !isNaN(client_value)) {
      return {
        text: retail_value > client_value
          ? `${formatMoney(retail_value)} ${charged_currency}`
          : `${formatMoney(client_value)} ${charged_currency}`,
      };
    } else if (!isNaN(client_value)) {
      return {
        text: `${formatMoney(client_value)} ${charged_currency}`,
      };
    } else {
      return {
        text: `${formatMoney(retail_value)} ${charged_currency}`
      };
    }
  }
};

const FeePercentageColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Percentage> = {
  header: "Fee %",
  type: BASE_COLUMN_TYPES.Percentage,
  percentageOptions: { decimalPlaces: 2 },
  fields: ["fee_rate"],
  accessorKey: "fee_rate",
  canSort: true,
  orderByField: "fee_rate",
  width: 60,
  dataTransform: ({ fee_rate }) => ({
    percentage: fee_rate * 100,
  })
};

const FeeValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Fee Value",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["charged_fee", "charged_currency_converted"],
  accessorKey: "fee_value",
  canSort: true,
  orderByField: "fee_value",
  width: 100,
  justify: "flex-end",
  dataTransform: ({ charged_fee, charged_currency_converted }) => ({
    text: `${formatMoney(charged_fee)} ${charged_currency_converted}`
  })
};

const BillingPeriodColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Billing Period",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["billing_period_name"],
  accessorKey: "billing_period",
  canSort: true,
  orderByField: "billing_period",
  width: 120,
};

const StatusColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Status",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["charged"],
  accessorKey: "status",
  canSort: true,
  orderByField: "status",
  width: 80,
  dataTransform: ({ charged }) => ({
    text: charged ? "Billed" : "Pending",
  })
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  DateCreatedColumn,
  ReferenceColumn,
  ProjectNameColumn,
  EmailColumn,
  RetailValueColumn,
  ClientValueColumn,
  OrderValueColumn,
  FeePercentageColumn,
  FeeValueColumn,
  BillingPeriodColumn,
  StatusColumn,
];
